import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { httpAuth as http } from "../lib/api";
import { useAuth, formatApiError } from "../context/AuthContext";
import { Seo } from "../components/Seo";
import { SalesPanel } from "../components/SalesPanel";
import { toast } from "sonner";
import {
  Building2,
  LogOut,
  Users,
  Database,
  Download,
  Play,
  Pause,
  RefreshCw,
  Shield,
  Trash2,
  Plus,
  Copy,
  CheckCircle2,
  AlertCircle,
  Inbox,
  X,
  Mail,
  Phone,
  Tag,
  HardDrive,
  Upload,
  ShieldAlert,
} from "lucide-react";

/* -------------------- small helpers -------------------- */
const SectionHeader = ({ step, title, action }) => (
  <div className="flex items-baseline justify-between border-b border-[#0A0A0A] pb-2 mb-5">
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563]">
        Section {step}
      </div>
      <h2 className="font-display font-black text-2xl tracking-tight mt-0.5">{title}</h2>
    </div>
    {action}
  </div>
);

const BrutalistButton = ({ children, testid, variant = "dark", ...rest }) => {
  const base =
    "inline-flex items-center justify-center gap-2 border px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
  const variants = {
    dark: "bg-[#0A0A0A] text-white border-[#0A0A0A] hover:bg-[#002FA7] hover:border-[#002FA7]",
    ghost: "bg-white text-[#0A0A0A] border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white",
    danger: "bg-white text-[#FF3333] border-[#FF3333] hover:bg-[#FF3333] hover:text-white",
    success: "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700",
  };
  return (
    <button data-testid={testid} className={`${base} ${variants[variant]}`} {...rest}>
      {children}
    </button>
  );
};

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return s;
  }
};

/* -------------------- stats card -------------------- */
const StatsPanel = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    http.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  const items = stats
    ? [
        ["Institutions", stats.total_institutions?.toLocaleString("en-IN")],
        ["Banks", stats.total_banks?.toLocaleString("en-IN")],
        ["NBFCs & ARCs", stats.total_nbfcs?.toLocaleString("en-IN")],
        ["Verified", stats.total_detailed?.toLocaleString("en-IN")],
        ["Contacts", stats.total_contacts?.toLocaleString("en-IN")],
        ["States", stats.total_states?.toLocaleString("en-IN")],
      ]
    : [];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 border border-[#0A0A0A]" data-testid="admin-stats">
      {items.map(([k, v]) => (
        <div key={k} className="p-4 border-r border-b border-[#0A0A0A] last:border-r-0 lg:[&:nth-child(6n)]:border-r-0">
          <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#4B5563]">{k}</div>
          <div className="font-display font-black text-2xl mt-1">{v || "…"}</div>
        </div>
      ))}
    </div>
  );
};

/* -------------------- enrichment panel -------------------- */
const EnrichmentPanel = () => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const poll = useCallback(() => {
    http.get("/admin/enrich/status").then((r) => setState(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 5000);
    return () => clearInterval(t);
  }, [poll]);

  const trigger = async (scope) => {
    setBusy(true);
    try {
      await http.post("/admin/enrich/start", null, { params: { scope, limit: 500 } });
      toast.success(`Enrichment started (${scope})`);
      poll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    setBusy(true);
    try {
      await http.post("/admin/enrich/stop");
      toast.success("Stop requested — finishing current item");
      poll();
    } finally {
      setBusy(false);
    }
  };

  const running = !!state?.running;

  return (
    <div className="border border-[#0A0A0A] p-6 bg-[#F8F9FA]" data-testid="admin-enrichment">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#002FA7] text-white flex items-center justify-center">
            <Database className="w-5 h-5" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563]">
              Background worker
            </div>
            <div className="font-display font-bold text-lg leading-tight">
              LLM contact enrichment
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2" aria-live="polite">
          <div
            data-testid="enrichment-dot"
            className={`w-2.5 h-2.5 rounded-full ${running ? "bg-emerald-500 animate-pulse" : "bg-[#4B5563]"}`}
          />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em]">
            {running ? "running" : "idle"}
          </span>
        </div>
      </div>

      {state && (
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3 text-sm border-t border-[#E5E7EB] pt-4">
          <div><dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">Scope</dt><dd className="font-bold mt-0.5">{state.target_scope || "—"}</dd></div>
          <div><dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">Processed</dt><dd className="font-bold mt-0.5" data-testid="enrich-processed">{state.processed ?? 0}</dd></div>
          <div><dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">Updated</dt><dd className="font-bold mt-0.5" data-testid="enrich-updated">{state.updated ?? 0}</dd></div>
          <div><dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">Remaining</dt><dd className="font-bold mt-0.5" data-testid="enrich-remaining">{state.remaining ?? 0}</dd></div>
          <div className="col-span-2 sm:col-span-4"><dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">Last processed</dt><dd className="text-[#0A0A0A] mt-0.5 break-words">{state.last_name || "—"}</dd></div>
        </dl>
      )}

      <div className="mt-5 flex flex-wrap gap-3">
        <BrutalistButton
          testid="enrich-start-banks"
          onClick={() => trigger("banks")}
          disabled={busy || running}
        >
          <Play className="w-3.5 h-3.5" /> Enrich banks
        </BrutalistButton>
        <BrutalistButton
          testid="enrich-start-nbfcs"
          onClick={() => trigger("all_nbfcs_continuous")}
          disabled={busy || running}
        >
          <Play className="w-3.5 h-3.5" /> Continuous NBFC sweep
        </BrutalistButton>
        <BrutalistButton
          testid="enrich-stop"
          variant="danger"
          onClick={stop}
          disabled={busy || !running}
        >
          <Pause className="w-3.5 h-3.5" /> Stop
        </BrutalistButton>
        <BrutalistButton testid="enrich-refresh" variant="ghost" onClick={poll}>
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </BrutalistButton>
      </div>
    </div>
  );
};

/* -------------------- do-not-contact list -------------------- */
const DncPanel = () => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const url = q ? `/admin/dnc?q=${encodeURIComponent(q)}` : "/admin/dnc";
      const { data } = await http.get(url);
      setData(data);
    } catch {
      setData({ total: 0, matched: 0, items: [] });
    }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const removeEntry = async (entry) => {
    const key = entry.slug || entry.raw_name;
    if (!window.confirm(`Remove "${entry.raw_name}" from the DNC list?`)) return;
    try {
      await http.delete(`/admin/dnc/${encodeURIComponent(key)}`);
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Remove failed"));
    }
  };

  const runBulk = async () => {
    if (!bulkText.trim()) return;
    setBusy(true);
    const t = toast.loading("Matching names against database…");
    try {
      const { data } = await http.post("/admin/dnc/import", {
        raw_text: bulkText,
        reason: "existing_customer",
        min_match_score: 0.90,
      });
      toast.success(
        `Parsed ${data.parsed} · matched ${data.matched} · unmatched ${data.unmatched}`,
        { id: t },
      );
      setBulkText("");
      setBulkOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Import failed"), { id: t });
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;

  return (
    <div className="border border-[#0A0A0A] p-6 bg-[#F8F9FA]" data-testid="admin-dnc">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <ShieldAlert className="w-4 h-4" />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">
              Do-not-contact list
            </div>
            <div className="font-bold text-sm mt-0.5" data-testid="dnc-summary">
              {data.total} entries · {data.matched} matched · {data.total - data.matched} pending review
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          <BrutalistButton testid="dnc-bulk-toggle" variant="ghost" onClick={() => setBulkOpen(v => !v)}>
            <Plus className="w-3.5 h-3.5" /> Bulk add
          </BrutalistButton>
          <BrutalistButton testid="dnc-refresh" variant="ghost" onClick={load}>
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </BrutalistButton>
        </div>
      </div>

      <p className="text-xs text-[#4B5563] mb-4 leading-relaxed">
        Institutions on this list will <span className="font-bold">never</span> receive
        marketing, sales outreach or any system-generated message. Used by the
        upcoming PrivateCourt sales engine. Add existing customers, partners and
        regulators.
      </p>

      {bulkOpen ? (
        <div className="mb-5 border border-[#0A0A0A] p-4 bg-white">
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-2">
            Paste one institution per line (names, columns, copy-paste from any doc all work)
          </label>
          <textarea
            data-testid="dnc-bulk-input"
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            rows={8}
            className="w-full font-mono text-xs border border-[#0A0A0A] p-3 mb-3"
            placeholder="HDFC BANK LIMITED&#10;BAJAJ FINANCE LIMITED&#10;..."
          />
          <div className="flex gap-3">
            <BrutalistButton testid="dnc-bulk-submit" onClick={runBulk} disabled={busy || !bulkText.trim()}>
              {busy ? "Matching…" : "Run import"}
            </BrutalistButton>
            <BrutalistButton testid="dnc-bulk-cancel" variant="ghost" onClick={() => { setBulkOpen(false); setBulkText(""); }}>
              Cancel
            </BrutalistButton>
          </div>
        </div>
      ) : null}

      <input
        data-testid="dnc-search"
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by name…"
        className="w-full border border-[#0A0A0A] p-2 text-sm font-mono mb-3"
      />

      <div className="border border-[#0A0A0A] max-h-96 overflow-auto" data-testid="dnc-list">
        <table className="w-full text-xs">
          <thead className="bg-[#0A0A0A] text-white">
            <tr>
              <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Name on file</th>
              <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Matched institution</th>
              <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Score</th>
              <th className="text-left px-3 py-2 font-mono uppercase tracking-wider">Reason</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 ? (
              <tr><td colSpan="5" className="text-center py-6 text-[#4B5563]">No entries.</td></tr>
            ) : data.items.map((e, i) => (
              <tr key={`${e.slug}-${e.raw_name}-${i}`} className="border-t border-[#0A0A0A]/15 hover:bg-white">
                <td className="px-3 py-2 font-mono">{e.raw_name}</td>
                <td className="px-3 py-2">{e.name || <span className="text-[#FF3333]">unmatched</span>}</td>
                <td className="px-3 py-2 font-mono">{e.match_score?.toFixed?.(2) ?? "—"}</td>
                <td className="px-3 py-2 font-mono text-[#4B5563]">{e.reason}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    data-testid={`dnc-remove-${e.slug || i}`}
                    onClick={() => removeEntry(e)}
                    className="text-[#FF3333] hover:underline font-mono text-[10px] uppercase tracking-wider"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* -------------------- S3 nightly backup panel -------------------- */
const S3BackupPanel = () => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/backup/s3/status");
      setState(data);
    } catch {
      setState({ configured: false });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    if (busy) return;
    setBusy(true);
    const t = toast.loading("Running mongodump → S3 upload (may take 1-3 min)…");
    try {
      const { data } = await http.post("/admin/backup/s3/run");
      toast.success(
        `Backup OK: ${(data.size_bytes / 1e6).toFixed(1)} MB uploaded to s3://${data.bucket}/${data.s3_key}`,
        { id: t },
      );
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Backup failed"), { id: t });
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;

  const configured = state.configured;
  const last = state.last;
  const lastOk = last?.ok;
  return (
    <div className="border border-[#0A0A0A] p-6 bg-[#F8F9FA]" data-testid="admin-s3-backup">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <HardDrive className="w-4 h-4" />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">
              S3 nightly backup
            </div>
            <div className="font-bold text-sm mt-0.5">
              {!configured
                ? "Not configured (set AWS_S3_BACKUP_BUCKET)"
                : lastOk === true
                  ? "Last run succeeded"
                  : lastOk === false
                    ? "Last run FAILED"
                    : "Never run yet"}
            </div>
          </div>
        </div>
        <div
          data-testid="backup-dot"
          className={`w-2.5 h-2.5 rounded-full ${
            !configured
              ? "bg-[#9CA3AF]"
              : lastOk === true
                ? "bg-emerald-500"
                : lastOk === false
                  ? "bg-[#FF3333] animate-pulse"
                  : "bg-[#FFA500]"
          }`}
        />
      </div>

      <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-5">
        <div>
          <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">Bucket</dt>
          <dd className="font-bold mt-0.5 break-all" data-testid="backup-bucket">
            {state.bucket || "—"}
          </dd>
        </div>
        <div>
          <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">Region</dt>
          <dd className="font-bold mt-0.5" data-testid="backup-region">
            {state.region || "—"}
          </dd>
        </div>
        <div>
          <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">Last run</dt>
          <dd className="font-bold mt-0.5" data-testid="backup-last">
            {fmtDate(last?.started_at)}
          </dd>
        </div>
        <div>
          <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">Retention</dt>
          <dd className="font-bold mt-0.5" data-testid="backup-retention">
            {state.retention_days} days
          </dd>
        </div>
      </dl>

      {last?.error ? (
        <div className="mb-4 p-3 bg-white border border-[#FF3333] text-xs font-mono text-[#FF3333]" data-testid="backup-error">
          {last.error}
        </div>
      ) : null}

      <p className="text-xs text-[#4B5563] mb-4 leading-relaxed">
        Nightly run at <span className="font-mono font-bold">02:00 IST</span>.
        MongoDB is dumped, gzipped, AES256-encrypted, uploaded to S3; archives older
        than the retention window are automatically pruned.
      </p>

      <div className="flex flex-wrap gap-3">
        <BrutalistButton testid="backup-run" onClick={runNow} disabled={busy || !configured}>
          <Upload className={`w-3.5 h-3.5 ${busy ? "animate-pulse" : ""}`} />
          {busy ? "Running…" : "Backup now"}
        </BrutalistButton>
        <BrutalistButton testid="backup-refresh" variant="ghost" onClick={load}>
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </BrutalistButton>
      </div>
    </div>
  );
};

/* -------------------- redis cache panel -------------------- */
const CachePanel = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/cache/status");
      setStatus(data);
    } catch {
      setStatus({ redis_ok: false });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rebuild = async () => {
    if (busy) return;
    setBusy(true);
    const t = toast.loading("Rebuilding Redis cache — this takes ~60-90 seconds…");
    try {
      const { data } = await http.post("/admin/cache/rebuild");
      toast.success(`Cache rebuilt: v${data.version} · ${data.count} docs · ${data.elapsed_seconds}s`, { id: t });
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Rebuild failed"), { id: t });
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  const ok = status.redis_ok;
  return (
    <div className="border border-[#0A0A0A] p-6 bg-[#F8F9FA]" data-testid="admin-cache">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Database className="w-4 h-4" />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">
              Redis read-cache
            </div>
            <div className="font-bold text-sm mt-0.5">
              {ok ? "Serving from Redis" : "Unavailable — serving from Mongo"}
            </div>
          </div>
        </div>
        <div
          data-testid="cache-dot"
          className={`w-2.5 h-2.5 rounded-full ${
            ok ? "bg-emerald-500 animate-pulse" : "bg-[#FF3333]"
          }`}
        />
      </div>

      <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-5">
        <div>
          <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">Version</dt>
          <dd className="font-bold mt-0.5" data-testid="cache-version">
            {status.active_version ? `v${status.active_version}` : "—"}
          </dd>
        </div>
        <div>
          <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">Memory</dt>
          <dd className="font-bold mt-0.5" data-testid="cache-memory">
            {status.used_memory_human || "—"}
          </dd>
        </div>
        <div>
          <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">Last built</dt>
          <dd className="font-bold mt-0.5" data-testid="cache-last-build">
            {fmtDate(status.last_build_at)}
          </dd>
        </div>
        <div>
          <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">Breaker</dt>
          <dd className="font-bold mt-0.5" data-testid="cache-breaker">
            {status.breaker_open ? `OPEN (${status.breaker_fails} fails)` : "closed"}
          </dd>
        </div>
      </dl>

      <p className="text-xs text-[#4B5563] mb-4 leading-relaxed">
        Nightly rebuild runs automatically at <span className="font-mono font-bold">01:00 IST</span>.
        Use <span className="font-bold">Rebuild now</span> only when you need every
        enriched contact update visible immediately on the public site.
      </p>

      <div className="flex flex-wrap gap-3">
        <BrutalistButton testid="cache-rebuild" onClick={rebuild} disabled={busy}>
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
          {busy ? "Rebuilding…" : "Rebuild now"}
        </BrutalistButton>
        <BrutalistButton testid="cache-refresh" variant="ghost" onClick={load}>
          <RefreshCw className="w-3.5 h-3.5" /> Refresh status
        </BrutalistButton>
      </div>
    </div>
  );
};

/* -------------------- backups panel -------------------- */
const BackupsPanel = () => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/backup/list");
      setItems(data.backups || []);
    } catch (e) {
      if (e.response?.status !== 403) toast.error("Failed to list backups");
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createBackup = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/admin/backup/create");
      toast.success(`Backup created · ${(data.size_bytes / 1024 / 1024).toFixed(2)} MB`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const downloadUrl = (filename) =>
    `${http.defaults.baseURL}/admin/backup/download?filename=${encodeURIComponent(filename)}`;

  return (
    <div data-testid="admin-backups">
      <div className="flex flex-wrap gap-3 mb-4">
        <BrutalistButton testid="backup-create" onClick={createBackup} disabled={busy}>
          <Plus className="w-3.5 h-3.5" /> {busy ? "Creating…" : "Create backup"}
        </BrutalistButton>
        <BrutalistButton testid="backup-refresh" variant="ghost" onClick={load}>
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </BrutalistButton>
      </div>
      {items == null ? (
        <div className="font-mono text-xs text-[#4B5563]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-6 border border-dashed border-[#D1D5DB] text-sm text-[#4B5563]">
          No backups yet. Click "Create backup" to generate one.
        </div>
      ) : (
        <div className="border border-[#0A0A0A]">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-white font-mono text-[10px] uppercase tracking-[0.2em]">
              <tr>
                <th className="p-3 text-left">Filename</th>
                <th className="p-3 text-left">Size</th>
                <th className="p-3 text-left">Created</th>
                <th className="p-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.filename} className="border-t border-[#E5E7EB]" data-testid={`backup-row-${b.filename}`}>
                  <td className="p-3 font-mono text-xs">{b.filename}</td>
                  <td className="p-3">{(b.size_bytes / 1024 / 1024).toFixed(2)} MB</td>
                  <td className="p-3 text-[#4B5563]">{fmtDate(b.modified_at)}</td>
                  <td className="p-3 text-right">
                    <a
                      href={downloadUrl(b.filename)}
                      data-testid={`backup-download-${b.filename}`}
                      className="inline-flex items-center gap-1 border border-[#0A0A0A] px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-bold hover:bg-[#0A0A0A] hover:text-white"
                      download
                    >
                      <Download className="w-3 h-3" /> Download
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* -------------------- users panel -------------------- */
const UsersPanel = ({ me }) => {
  const [users, setUsers] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "user" });
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/users");
      setUsers(data.users || []);
    } catch (e) {
      if (e.response?.status === 403) setUsers([]);
      else toast.error("Failed to load users");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await http.post("/auth/users", form);
      toast.success(`User ${form.email} created`);
      setForm({ email: "", name: "", password: "", role: "user" });
      setShowAdd(false);
      load();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await http.delete(`/auth/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const resetPwd = async (u) => {
    const p = window.prompt(`New password for ${u.email} (min 8 chars):`);
    if (!p) return;
    try {
      await http.post(`/auth/users/${u.id}/password`, { password: p });
      toast.success(`Password reset for ${u.email}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const isAdmin = me?.role === "admin";
  if (!isAdmin) {
    return (
      <div className="p-6 border border-dashed border-[#D1D5DB] text-sm text-[#4B5563]">
        You need admin role to manage users.
      </div>
    );
  }

  return (
    <div data-testid="admin-users">
      <div className="flex flex-wrap gap-3 mb-4">
        <BrutalistButton testid="user-add-toggle" onClick={() => setShowAdd((v) => !v)}>
          <Plus className="w-3.5 h-3.5" /> {showAdd ? "Cancel" : "Add user"}
        </BrutalistButton>
        <BrutalistButton testid="user-refresh" variant="ghost" onClick={load}>
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </BrutalistButton>
      </div>

      {showAdd && (
        <form
          onSubmit={submit}
          className="border border-[#0A0A0A] p-5 bg-[#F8F9FA] mb-6 grid grid-cols-1 md:grid-cols-2 gap-4"
          data-testid="user-add-form"
        >
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] block mb-1">
              Email
            </label>
            <input
              type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="user-add-email"
              className="w-full border border-[#0A0A0A] px-3 py-2 text-sm font-mono bg-white"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] block mb-1">
              Name
            </label>
            <input
              type="text" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="user-add-name"
              className="w-full border border-[#0A0A0A] px-3 py-2 text-sm font-mono bg-white"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] block mb-1">
              Password (min 8)
            </label>
            <input
              type="text" required minLength={8} value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="user-add-password"
              className="w-full border border-[#0A0A0A] px-3 py-2 text-sm font-mono bg-white"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] block mb-1">
              Role
            </label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              data-testid="user-add-role"
              className="w-full border border-[#0A0A0A] px-3 py-2 text-sm font-mono bg-white"
            >
              <option value="user">user — read-only admin access</option>
              <option value="admin">admin — full control</option>
            </select>
          </div>
          {err && (
            <div className="col-span-full flex items-start gap-2 border-l-4 border-[#FF3333] bg-red-50 px-3 py-2 text-xs text-red-900">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              <span>{err}</span>
            </div>
          )}
          <div className="col-span-full">
            <BrutalistButton testid="user-add-submit" type="submit" disabled={busy}>
              <CheckCircle2 className="w-3.5 h-3.5" /> {busy ? "Creating…" : "Create user"}
            </BrutalistButton>
          </div>
        </form>
      )}

      {users == null ? (
        <div className="font-mono text-xs text-[#4B5563]">Loading…</div>
      ) : users.length === 0 ? (
        <div className="p-6 border border-dashed border-[#D1D5DB] text-sm text-[#4B5563]">
          No users yet.
        </div>
      ) : (
        <div className="border border-[#0A0A0A] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-white font-mono text-[10px] uppercase tracking-[0.2em]">
              <tr>
                <th className="p-3 text-left">Email</th>
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Role</th>
                <th className="p-3 text-left">Created</th>
                <th className="p-3 text-left">Last login</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-[#E5E7EB]" data-testid={`user-row-${u.email}`}>
                  <td className="p-3 font-mono text-xs">
                    {u.email}
                    {u.id === me.id && (
                      <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.2em] bg-[#002FA7] text-white px-1.5 py-0.5">you</span>
                    )}
                  </td>
                  <td className="p-3">{u.name || "—"}</td>
                  <td className="p-3">
                    <span className={`inline-block font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border ${u.role === "admin" ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-[#0A0A0A]"}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="p-3 text-[#4B5563] text-xs">{fmtDate(u.created_at)}</td>
                  <td className="p-3 text-[#4B5563] text-xs">{fmtDate(u.last_login_at)}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => resetPwd(u)}
                      data-testid={`user-reset-${u.email}`}
                      className="border border-[#0A0A0A] px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] font-bold hover:bg-[#0A0A0A] hover:text-white mr-2"
                    >
                      Reset pwd
                    </button>
                    <button
                      onClick={() => remove(u)}
                      disabled={u.id === me.id}
                      data-testid={`user-delete-${u.email}`}
                      className="border border-[#FF3333] text-[#FF3333] px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] font-bold hover:bg-[#FF3333] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="w-3 h-3 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* -------------------- complaints panel -------------------- */
const ISSUE_LABELS = {
  loan_harassment: "Loan harassment",
  wrong_cibil: "Wrong CIBIL",
  unauthorised_charges: "Unauthorised charges",
  sarfaesi_notice: "SARFAESI notice",
  grievance_unanswered: "Grievance unanswered",
  recovery_agent_misconduct: "Recovery agent misconduct",
  other: "Other",
};

const STATUS_STYLES = {
  new: "bg-[#FF3333] text-white border-[#FF3333]",
  reviewing: "bg-[#002FA7] text-white border-[#002FA7]",
  resolved: "bg-emerald-600 text-white border-emerald-600",
  rejected: "bg-[#4B5563] text-white border-[#4B5563]",
  spam: "bg-amber-600 text-white border-amber-600",
};

const ComplaintsPanel = () => {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null); // full doc
  const [drawerBusy, setDrawerBusy] = useState(false);
  const [newNote, setNewNote] = useState("");

  const load = useCallback(async () => {
    try {
      const params = { page, page_size: 25 };
      if (filterStatus) params.status = filterStatus;
      if (q) params.q = q;
      const { data } = await http.get("/complaints", { params });
      setItems(data.results);
      setTotal(data.total);
    } catch (e) {
      if (e.response?.status !== 403) toast.error("Failed to load complaints");
      setItems([]);
    }
  }, [filterStatus, q, page]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await http.get("/complaints/stats");
      setStats(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const openDetail = async (id) => {
    try {
      const { data } = await http.get(`/complaints/${id}`);
      setSelected(data);
      setNewNote("");
    } catch {
      toast.error("Failed to load complaint");
    }
  };

  const patchSelected = async (patch) => {
    if (!selected) return;
    setDrawerBusy(true);
    try {
      const { data } = await http.patch(`/complaints/${selected.id}`, patch);
      setSelected(data);
      setNewNote("");
      toast.success("Updated");
      load();
      loadStats();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDrawerBusy(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / 25));

  return (
    <div data-testid="admin-complaints">
      {/* Summary tiles */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 border border-[#0A0A0A] mb-5" data-testid="complaints-stats">
          {[
            ["Total (non-spam)", stats.total, null],
            ["New", stats.new || 0, "new"],
            ["Reviewing", stats.by_status?.reviewing || 0, "reviewing"],
            ["Resolved", stats.by_status?.resolved || 0, "resolved"],
            ["Spam", stats.by_status?.spam || 0, "spam"],
          ].map(([k, v, st]) => (
            <button
              key={k}
              onClick={() => { setFilterStatus(st || ""); setPage(1); }}
              data-testid={`complaints-tile-${st || "all"}`}
              className={`p-4 border-r border-b border-[#0A0A0A] last:border-r-0 sm:[&:nth-child(5n)]:border-r-0 text-left hover:bg-[#F8F9FA] transition-colors ${filterStatus === (st || "") ? "bg-[#F8F9FA]" : ""}`}
            >
              <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#4B5563]">{k}</div>
              <div className="font-display font-black text-2xl mt-1">{v}</div>
            </button>
          ))}
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div className="flex-1 min-w-[220px]">
          <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] block mb-1">Search</label>
          <input
            type="search" value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Ticket / name / email / institution…"
            data-testid="complaints-search"
            className="w-full border border-[#0A0A0A] px-3 py-2 text-sm font-mono bg-white"
          />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] block mb-1">Status</label>
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
            data-testid="complaints-filter-status"
            className="border border-[#0A0A0A] px-3 py-2 text-sm font-mono bg-white min-w-[160px]"
          >
            <option value="">All (excl. spam)</option>
            <option value="new">New</option>
            <option value="reviewing">Reviewing</option>
            <option value="resolved">Resolved</option>
            <option value="rejected">Rejected</option>
            <option value="spam">Spam</option>
          </select>
        </div>
        <BrutalistButton testid="complaints-refresh" variant="ghost" onClick={() => { load(); loadStats(); }}>
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </BrutalistButton>
      </div>

      {/* Table */}
      {items == null ? (
        <div className="font-mono text-xs text-[#4B5563]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-10 border border-dashed border-[#D1D5DB] text-center text-sm text-[#4B5563]" data-testid="complaints-empty">
          <Inbox className="w-6 h-6 mx-auto mb-2 opacity-60" />
          No complaints matching the current filter.
        </div>
      ) : (
        <div className="border border-[#0A0A0A] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-white font-mono text-[10px] uppercase tracking-[0.2em]">
              <tr>
                <th className="p-3 text-left">Ticket</th>
                <th className="p-3 text-left">Received</th>
                <th className="p-3 text-left">Complainant</th>
                <th className="p-3 text-left">Institution</th>
                <th className="p-3 text-left">Issue</th>
                <th className="p-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => openDetail(c.id)}
                  className="border-t border-[#E5E7EB] hover:bg-[#F8F9FA] cursor-pointer"
                  data-testid={`complaint-row-${c.ticket_no}`}
                >
                  <td className="p-3 font-mono text-xs font-bold">{c.ticket_no}</td>
                  <td className="p-3 text-[#4B5563] text-xs">{fmtDate(c.created_at)}</td>
                  <td className="p-3">
                    <div className="font-bold">{c.name}</div>
                    <div className="text-[11px] text-[#4B5563] font-mono">{c.email}</div>
                  </td>
                  <td className="p-3 text-xs max-w-[220px] truncate">{c.institution_name}</td>
                  <td className="p-3 text-xs">{ISSUE_LABELS[c.issue_type] || c.issue_type}</td>
                  <td className="p-3">
                    <span className={`inline-block font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border ${STATUS_STYLES[c.status] || "border-[#0A0A0A]"}`}>
                      {c.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {items && items.length > 0 && totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 font-mono text-xs">
          <div className="text-[#4B5563]">
            Page {page} of {totalPages} · {total} record{total === 1 ? "" : "s"}
          </div>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="border border-[#0A0A0A] px-3 py-1 hover:bg-[#0A0A0A] hover:text-white disabled:opacity-40"
              data-testid="complaints-prev"
            >Prev</button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="border border-[#0A0A0A] px-3 py-1 hover:bg-[#0A0A0A] hover:text-white disabled:opacity-40"
              data-testid="complaints-next"
            >Next</button>
          </div>
        </div>
      )}

      {/* Drawer */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex"
          data-testid="complaint-drawer"
          onClick={(e) => { if (e.target === e.currentTarget) setSelected(null); }}
        >
          <div className="flex-1 bg-black/50" />
          <div className="w-full max-w-[640px] h-full bg-white border-l border-[#0A0A0A] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-[#0A0A0A] px-6 py-4 flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563]">
                  {fmtDate(selected.created_at)}
                </div>
                <div className="font-display font-black text-2xl tracking-tight">
                  {selected.ticket_no}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`inline-block font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${STATUS_STYLES[selected.status] || "border-[#0A0A0A]"}`}>
                  {selected.status}
                </span>
                <button
                  onClick={() => setSelected(null)}
                  data-testid="complaint-drawer-close"
                  className="text-[#4B5563] hover:text-[#0A0A0A]"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Contact */}
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] mb-2">
                  Complainant
                </div>
                <div className="font-bold text-lg">{selected.name}</div>
                <div className="mt-1 text-sm text-[#4B5563] flex flex-col gap-1">
                  <a href={`mailto:${selected.email}`} className="flex items-center gap-2 hover:text-[#002FA7]">
                    <Mail className="w-3.5 h-3.5" /> {selected.email}
                  </a>
                  <a href={`tel:${selected.phone}`} className="flex items-center gap-2 hover:text-[#002FA7]">
                    <Phone className="w-3.5 h-3.5" /> {selected.phone}
                  </a>
                </div>
              </div>

              {/* Context */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] mb-1">Institution</div>
                  {selected.institution_slug ? (
                    <Link to={`/${selected.institution_slug.startsWith("bank-") ? "bank" : "nbfc"}/${selected.institution_slug}`}
                          className="text-[#002FA7] underline break-words">
                      {selected.institution_name}
                    </Link>
                  ) : (
                    <div>{selected.institution_name}</div>
                  )}
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] mb-1">Issue type</div>
                  <div className="font-bold">{ISSUE_LABELS[selected.issue_type] || selected.issue_type}</div>
                </div>
                {selected.loan_ref && (
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] mb-1">Loan ref</div>
                    <div className="font-mono">{selected.loan_ref}</div>
                  </div>
                )}
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] mb-1">IP hash</div>
                  <div className="font-mono text-xs">{selected.ip_hash}</div>
                </div>
              </div>

              {/* Description */}
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] mb-2">Description</div>
                <div className="border border-[#E5E7EB] p-4 text-sm whitespace-pre-wrap leading-relaxed bg-[#F8F9FA]">
                  {selected.description}
                </div>
              </div>

              {/* Status & assignment */}
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] mb-2">Status & assignment</div>
                <div className="flex flex-wrap gap-2">
                  {["new", "reviewing", "resolved", "rejected", "spam"].map((s) => (
                    <button
                      key={s} onClick={() => patchSelected({ status: s })}
                      disabled={drawerBusy || selected.status === s}
                      data-testid={`complaint-status-${s}`}
                      className={`font-mono text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border ${selected.status === s ? STATUS_STYLES[s] : "border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"} disabled:opacity-60`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Tag className="w-3.5 h-3.5 text-[#4B5563]" />
                  <input
                    type="text"
                    placeholder="assign to (email)"
                    defaultValue={selected.assigned_to || ""}
                    onBlur={(e) => {
                      if (e.target.value !== (selected.assigned_to || "")) {
                        patchSelected({ assigned_to: e.target.value.trim() });
                      }
                    }}
                    data-testid="complaint-assign"
                    className="flex-1 border border-[#0A0A0A] px-2 py-1 text-xs font-mono"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] mb-2">
                  Admin notes ({(selected.admin_notes || []).length})
                </div>
                <div className="space-y-2 max-h-[240px] overflow-y-auto mb-3">
                  {(selected.admin_notes || []).map((n) => (
                    <div key={n.id} className="border-l-2 border-[#002FA7] bg-[#F8F9FA] px-3 py-2 text-xs">
                      <div className="font-mono text-[10px] text-[#4B5563] mb-1">
                        {n.author_email} · {fmtDate(n.at)}
                      </div>
                      <div className="whitespace-pre-wrap">{n.text}</div>
                    </div>
                  ))}
                  {(!selected.admin_notes || selected.admin_notes.length === 0) && (
                    <div className="text-xs text-[#4B5563] italic">No notes yet.</div>
                  )}
                </div>
                <textarea
                  rows={3} value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a private note…"
                  data-testid="complaint-note-input"
                  className="w-full border border-[#0A0A0A] px-3 py-2 text-sm font-mono"
                />
                <div className="mt-2 flex justify-end">
                  <BrutalistButton
                    testid="complaint-note-save"
                    disabled={drawerBusy || !newNote.trim()}
                    onClick={() => patchSelected({ add_note: newNote })}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Add note
                  </BrutalistButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* -------------------- main page -------------------- */
export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const copyId = () => {
    if (!user) return;
    navigator.clipboard?.writeText(user.id);
    toast.success("User ID copied");
  };

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A]">
      <Seo title="Admin Dashboard" description="BankDesks.com admin console" path="/admin" noindex />

      <header className="border-b border-[#0A0A0A] bg-white sticky top-0 z-20">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-4 flex-wrap">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-home-link">
            <div className="w-9 h-9 bg-[#002FA7] text-white flex items-center justify-center">
              <Building2 className="w-5 h-5" strokeWidth={2.25} />
            </div>
            <div className="leading-tight">
              <div className="font-display font-black text-sm tracking-tight">BankDesks.com</div>
              <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#4B5563]">admin console</div>
            </div>
          </Link>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="hidden sm:flex items-center gap-2 border border-[#0A0A0A] px-3 py-1.5" data-testid="admin-user-pill">
              <Shield className="w-3.5 h-3.5" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em]">
                {user?.email}
              </span>
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] bg-[#002FA7] text-white px-1.5 py-0.5">
                {user?.role}
              </span>
              <button
                onClick={copyId}
                title="Copy user ID"
                data-testid="admin-copy-id"
                className="ml-1 text-[#4B5563] hover:text-[#0A0A0A]"
              >
                <Copy className="w-3 h-3" />
              </button>
            </div>
            <BrutalistButton testid="admin-logout-btn" variant="ghost" onClick={doLogout}>
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </BrutalistButton>
          </div>
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10 space-y-14">
        {/* Greeting */}
        <section>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] mb-2">
            Welcome back
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
            {user?.name || user?.email?.split("@")[0] || "Operator"}.
          </h1>
          <p className="mt-3 text-[#4B5563] max-w-2xl">
            Manage background enrichment, review coverage statistics, take database
            backups and control who else can access this panel.
          </p>
        </section>

        <section>
          <SectionHeader step="01" title="Coverage snapshot" />
          <StatsPanel />
        </section>

        <section>
          <SectionHeader step="02" title="Data enrichment" />
          <EnrichmentPanel />
        </section>

        <section aria-labelledby="cache-heading" className="space-y-5">
          <SectionHeader step="02b" title="Redis read-cache" />
          <CachePanel />
        </section>

        <section aria-labelledby="s3-backup-heading" className="space-y-5">
          <SectionHeader step="02c" title="S3 nightly backup" />
          <S3BackupPanel />
        </section>

        <section aria-labelledby="dnc-heading" className="space-y-5">
          <SectionHeader step="02d" title="Do-not-contact list" />
          <DncPanel />
        </section>

        <section aria-labelledby="sales-heading" className="space-y-5">
          <SectionHeader
            step="02e"
            title="PrivateCourt sales outreach"
            action={
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">
                <Mail className="inline w-3 h-3 -mt-0.5 mr-1" /> AI · approval · DNC
              </span>
            }
          />
          <SalesPanel />
        </section>

        <section>
          <SectionHeader
            step="03"
            title="Resolution requests"
            action={
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">
                <Inbox className="inline w-3 h-3 -mt-0.5 mr-1" /> public submissions
              </span>
            }
          />
          <ComplaintsPanel />
        </section>

        {user?.role === "admin" && (
          <section>
            <SectionHeader
              step="04"
              title="User management"
              action={
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">
                  <Users className="inline w-3 h-3 -mt-0.5 mr-1" /> admin only
                </span>
              }
            />
            <UsersPanel me={user} />
          </section>
        )}

        <section>
          <SectionHeader
            step={user?.role === "admin" ? "05" : "04"}
            title="Database backups"
          />
          <BackupsPanel />
        </section>
      </main>
    </div>
  );
}
